import React from 'react';

export type LogoKey =
  | 'default'
  | 'gradient-orb'
  | 'mesh-blob'
  | 'glass-ring'
  | 'starburst'
  | 'glass-panel'
  | 'aurora'
  | 'dot-matrix'
  | 'saturn';

/* ── Inline CSS for logo animations ── */
export const logoStyles = `
@keyframes ai-orbSpin { to { transform: rotate(360deg); } }
@keyframes ai-coreBreath {
  0%,100% { box-shadow: 0 0 10px rgba(99,102,241,0.8); transform: scale(1); }
  50%      { box-shadow: 0 0 18px rgba(99,102,241,1); transform: scale(1.08); }
}
@keyframes ai-blobMorph {
  0%,100% { border-radius: 40% 60% 55% 45% / 45% 55% 60% 40%; transform: rotate(0deg); }
  33%      { border-radius: 55% 45% 40% 60% / 60% 40% 55% 45%; transform: rotate(4deg); }
  66%      { border-radius: 45% 55% 60% 40% / 40% 60% 45% 55%; transform: rotate(-4deg); }
}
@keyframes ai-grSpin { to { transform: rotate(360deg); } }
@keyframes ai-sparkRotate { to { transform: rotate(360deg); } }
@keyframes ai-glassGlow {
  0%,100% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), 0 4px 16px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,102,241,0.2); }
  50%      { box-shadow: inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.45), 0 0 12px rgba(99,102,241,0.15); }
}
@keyframes ai-auroraShift { 0%,100% { filter: hue-rotate(0deg); } 50% { filter: hue-rotate(30deg); } }
@keyframes ai-auroraCore { 0%,100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(0.85); opacity: 0.7; } }
@keyframes ai-dmPulse { 0%,100% { opacity: 0.8; } 50% { opacity: 1; } }
@keyframes ai-saturnSpin { 0% { transform: rotateX(72deg) rotate(0deg); } 100% { transform: rotateX(72deg) rotate(360deg); } }
`;

/** Renders a single logo at given size */
export const LogoRenderer: React.FC<{ logoKey: LogoKey; size?: number; usePrimaryColor?: boolean }> = ({
  logoKey,
  size = 40,
  usePrimaryColor = false,
}) => {
  const s = size;
  const coreSize = Math.round(s * 0.35);
  const halfCore = Math.round(s * 0.175);

  if (logoKey === 'default') {
    return <img src="/assets/images/logo.svg" alt="Logo" width={s} height={s} />;
  }

  const primaryFilter = usePrimaryColor
    ? 'sepia(1) saturate(3) hue-rotate(90deg) brightness(0.9)'
    : undefined;

  const wrapWithFilter = (node: React.ReactElement) =>
    primaryFilter ? <div style={{ filter: primaryFilter }}>{node}</div> : node;

  switch (logoKey) {
    case 'gradient-orb':
      return wrapWithFilter(
        <div style={{ width: s, height: s, borderRadius: '50%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            width: s, height: s, borderRadius: '50%',
            background: 'conic-gradient(from 180deg at 50% 50%, #6366F1 0deg, #8B5CF6 90deg, #06B6D4 180deg, #6366F1 360deg)',
            animation: 'ai-orbSpin 4s linear infinite', position: 'relative',
          }}>
            <div style={{
              content: '', position: 'absolute', inset: 2, borderRadius: '50%',
              background: 'radial-gradient(circle at 40% 35%, #1E1B4B, #0A0A0F)',
            }} />
          </div>
          <div style={{
            position: 'absolute', width: coreSize, height: coreSize, borderRadius: '50%',
            background: 'radial-gradient(circle at 38% 32%, #A5B4FC, #6366F1 60%, #4338CA)',
            boxShadow: '0 0 10px rgba(99,102,241,0.8)', zIndex: 2,
            animation: 'ai-coreBreath 3s ease-in-out infinite',
          }} />
        </div>
      );

    case 'mesh-blob':
      return wrapWithFilter(
        <div style={{ width: s, height: s, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            width: s * 0.9, height: s * 0.9,
            borderRadius: '40% 60% 55% 45% / 45% 55% 60% 40%',
            background: 'linear-gradient(135deg, #F472B6 0%, #EC4899 35%, #A855F7 70%, #8B5CF6 100%)',
            boxShadow: '0 0 15px rgba(236,72,153,0.4), 0 0 30px rgba(168,85,247,0.2)',
            animation: 'ai-blobMorph 5s ease-in-out infinite',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: halfCore, height: halfCore, borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(4px)',
            }} />
          </div>
        </div>
      );

    case 'glass-ring':
      return wrapWithFilter(
        <div style={{ width: s, height: s, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', width: s, height: s, borderRadius: '50%',
            border: '1.5px solid rgba(56,189,248,0.3)', animation: 'ai-grSpin 8s linear infinite',
          }}>
            <div style={{
              position: 'absolute', top: -3, left: '50%', transform: 'translateX(-50%)',
              width: 5, height: 5, borderRadius: '50%',
              background: '#38BDF8', boxShadow: '0 0 8px #38BDF8, 0 0 16px rgba(56,189,248,0.6)',
            }} />
          </div>
          <div style={{
            position: 'absolute', width: s * 0.7, height: s * 0.7, borderRadius: '50%',
            border: '1px solid rgba(56,189,248,0.15)', animation: 'ai-grSpin 6s linear infinite reverse',
          }}>
            <div style={{
              position: 'absolute', bottom: -3, left: '50%', transform: 'translateX(-50%)',
              width: 4, height: 4, borderRadius: '50%',
              background: '#7DD3FC', boxShadow: '0 0 6px #7DD3FC',
            }} />
          </div>
          <div style={{
            width: s * 0.4, height: s * 0.4, borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 30%, rgba(186,230,253,0.9), rgba(14,165,233,0.6))',
            border: '1px solid rgba(186,230,253,0.3)', boxShadow: '0 0 10px rgba(14,165,233,0.5)',
          }} />
        </div>
      );

    case 'starburst': {
      const r = s / 2;
      return wrapWithFilter(
        <div style={{ width: s, height: s, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} style={{ animation: 'ai-sparkRotate 10s linear infinite' }}>
            <defs>
              <linearGradient id="aiSpkG" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FDE68A" />
                <stop offset="100%" stopColor="#F59E0B" />
              </linearGradient>
            </defs>
            <line x1={r} y1={s*0.1} x2={r} y2={s*0.3} stroke="url(#aiSpkG)" strokeWidth="2" strokeLinecap="round" />
            <line x1={r} y1={s*0.7} x2={r} y2={s*0.9} stroke="url(#aiSpkG)" strokeWidth="2" strokeLinecap="round" />
            <line x1={s*0.1} y1={r} x2={s*0.3} y2={r} stroke="url(#aiSpkG)" strokeWidth="2" strokeLinecap="round" />
            <line x1={s*0.7} y1={r} x2={s*0.9} y2={r} stroke="url(#aiSpkG)" strokeWidth="2" strokeLinecap="round" />
            <circle cx={r} cy={s*0.08} r={2} fill="#FDE68A" />
            <circle cx={r} cy={s*0.92} r={2} fill="#FDE68A" opacity={0.6} />
            <circle cx={s*0.08} cy={r} r={2} fill="#FDE68A" opacity={0.6} />
            <circle cx={s*0.92} cy={r} r={2} fill="#FDE68A" opacity={0.6} />
          </svg>
          <div style={{
            position: 'absolute', width: coreSize, height: coreSize, borderRadius: '50%',
            background: 'radial-gradient(circle, #FDE68A, #F59E0B 60%, #D97706)',
            boxShadow: '0 0 10px rgba(245,158,11,0.8)',
          }} />
        </div>
      );
    }

    case 'glass-panel': {
      const panelSize = s * 0.95;
      return wrapWithFilter(
        <div style={{
          width: panelSize, height: panelSize, borderRadius: s * 0.28,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 4px 16px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,102,241,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', overflow: 'hidden', animation: 'ai-glassGlow 4s ease-in-out infinite',
        }}>
          <svg width={s*0.5} height={s*0.5} viewBox="0 0 38 38" fill="none" style={{ position: 'relative', zIndex: 1 }}>
            <defs>
              <linearGradient id="aiSqG" x1="0" y1="0" x2="38" y2="38" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
                <stop offset="100%" stopColor="rgba(165,180,252,0.7)" />
              </linearGradient>
            </defs>
            <circle cx="19" cy="19" r="11" stroke="url(#aiSqG)" strokeWidth="1.5" fill="none" />
            <path d="M13 19h3M17 15h3M17 23h3M21 19h3" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="19" cy="19" r="2" fill="url(#aiSqG)" />
          </svg>
        </div>
      );
    }

    case 'aurora':
      return wrapWithFilter(
        <div style={{
          width: s, height: s, borderRadius: '50%', position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: `radial-gradient(ellipse at 30% 20%, rgba(52,211,153,0.7) 0%, transparent 50%),
              radial-gradient(ellipse at 70% 80%, rgba(99,102,241,0.7) 0%, transparent 50%),
              radial-gradient(ellipse at 80% 20%, rgba(56,189,248,0.5) 0%, transparent 40%), #060F1E`,
            animation: 'ai-auroraShift 6s ease-in-out infinite',
            border: '1px solid rgba(52,211,153,0.2)',
            boxShadow: '0 0 15px rgba(52,211,153,0.2), 0 0 30px rgba(99,102,241,0.15)',
          }} />
          <div style={{
            position: 'relative', zIndex: 2,
            width: coreSize, height: coreSize, borderRadius: '50%',
            background: 'rgba(255,255,255,0.9)', boxShadow: '0 0 10px rgba(255,255,255,0.5)',
            animation: 'ai-auroraCore 3s ease-in-out infinite',
          }} />
        </div>
      );

    case 'dot-matrix':
      return wrapWithFilter(
        <div style={{ width: s, height: s, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid #1A1A2E' }} />
          <svg width={s*0.9} height={s*0.9} viewBox="0 0 72 72" style={{ animation: 'ai-dmPulse 3s ease-in-out infinite' }}>
            <defs>
              <radialGradient id="aiDmG" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#F0ABFC" />
                <stop offset="100%" stopColor="#A855F7" />
              </radialGradient>
            </defs>
            <circle cx="36" cy="36" r="7" fill="url(#aiDmG)" />
            <circle cx="36" cy="36" r="12" fill="none" stroke="rgba(168,85,247,0.2)" strokeWidth="1" />
            <circle cx="19" cy="19" r="3" fill="#A855F7" opacity={0.6} />
            <circle cx="36" cy="15" r="3" fill="#A855F7" opacity={0.5} />
            <circle cx="53" cy="19" r="3" fill="#A855F7" opacity={0.6} />
            <circle cx="15" cy="36" r="3" fill="#A855F7" opacity={0.5} />
            <circle cx="57" cy="36" r="3" fill="#A855F7" opacity={0.5} />
            <circle cx="19" cy="53" r="3" fill="#A855F7" opacity={0.6} />
            <circle cx="36" cy="57" r="3" fill="#A855F7" opacity={0.5} />
            <circle cx="53" cy="53" r="3" fill="#A855F7" opacity={0.6} />
          </svg>
        </div>
      );

    case 'saturn':
      return wrapWithFilter(
        <div style={{ width: s, height: s, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', width: s * 0.8, height: s * 0.2, borderRadius: '50%',
            border: '1.5px solid rgba(167,139,250,0.2)',
            transform: 'rotateX(72deg)', animation: 'ai-saturnSpin 4s linear infinite reverse',
          }} />
          <div style={{
            position: 'absolute', width: s * 0.95, height: s * 0.28, borderRadius: '50%',
            border: '2px solid rgba(167,139,250,0.35)',
            transform: 'rotateX(72deg)', boxShadow: '0 0 8px rgba(167,139,250,0.2)',
            animation: 'ai-saturnSpin 6s linear infinite',
          }} />
          <div style={{
            width: s * 0.42, height: s * 0.42, borderRadius: '50%',
            background: 'radial-gradient(circle at 38% 32%, #C4B5FD, #7C3AED 55%, #4C1D95)',
            boxShadow: '0 0 10px rgba(124,58,237,0.7)', zIndex: 2,
            animation: 'ai-coreBreath 3.5s ease-in-out infinite',
          }} />
        </div>
      );

    default:
      return null;
  }
};
